import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid2,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Stack,
} from '@mui/material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import {
  DraftChapter,
  DraftManual,
  DraftPolicy,
  DraftPolicyItem,
  DraftPolicySection,
  DraftPolicySubSection,
  DraftPreface,
  DraftPrefaceItem,
} from 'views/App/Portal/Policy/_types/manuals/draft';
import SortableItem from 'helpers/sortableItems';
import { useManualStructureContext } from './context';
import { translate as t } from 'hooks/useTranslations';
import { ManualFilterOptions } from 'views/App/Portal/Policy/_types/enums';
import DraggableItem from './_components/DraggableItem';
import Loading from 'views/App/Portal/Loading';
import InactivityDialog from './_components/InactivityDialog';
import draftManualHelper from '../../helpers/draftManualHelper';
import useDraftPreface from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftPreface';
import useDraftPolicy from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftPolicy';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';

const ManualStructure: React.FC = () => {
  const {
    reorderedManual,
    filter,
    selectedChapter,
    isSaving,
    handleFilterChange,
    handleChapterClick,
    handleDragEndPrefaces,
    handleDragEndChapters,
    handleDragEndPolicies,
    unlockManual,
    handleSave,
    handleToggleState,
    getFilteredItems,
    sanitizeContent,
    showDialog,
    handleDialogConfirm,
    sidebarItem,
    setSidebarItem,
  } = useManualStructureContext();

  const prefaceSidebarQuery = useDraftPreface(
    reorderedManual?.id,
    sidebarItem?.type === 'preface' ? sidebarItem?.item.id : undefined
  );
  const policySidebarQuery = useDraftPolicy(
    reorderedManual?.id,
    sidebarItem?.type === 'policy' ? sidebarItem?.item.id : undefined
  );

  const [contentData, setContentData] = useState<DraftPolicy | DraftPreface | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [sidebarItemType, setSidebarItemType] = useState<string | null>(null);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);

  useEffect(() => {
    if (sidebarItem?.type === 'preface' && prefaceSidebarQuery.data) {
      setContentData(prefaceSidebarQuery?.data as DraftPreface);
      const date = prefaceSidebarQuery.data.updatedAt || prefaceSidebarQuery.data.createdAt;
      setUpdatedAt(date ? new Date(date) : null);
    } else if (sidebarItem?.type === 'policy' && prefaceSidebarQuery.data) {
      setContentData(policySidebarQuery?.data as DraftPolicy);
      const date = prefaceSidebarQuery.data.updatedAt || prefaceSidebarQuery.data.createdAt;
      setUpdatedAt(date ? new Date(date) : null);
    } else {
      setContentData(null);
      setUpdatedAt(null);
    }
  }, [sidebarItem, prefaceSidebarQuery.data, policySidebarQuery.data]);

  useEffect(() => {
    if (sidebarItem?.type) {
      setSidebarItemType(sidebarItem.type);
    }
  }, [sidebarItem]);

  useEffect(() => {
    setRightDrawerOpen(!!sidebarItem);
  }, [sidebarItem]);

  const getSectionIndex = (section: DraftPolicySection) => {
    if (policySidebarQuery?.data?.state !== 'DISABLED') {
      return `${draftManualHelper.getNumberManualNode(policySidebarQuery?.data, reorderedManual as DraftManual)}.${draftManualHelper.getEnabledSectionIndex(section?.id, policySidebarQuery?.data as DraftPolicy)}`;
    }
    return '';
  };

  const getSubSectionIndex = (section: DraftPolicySection, subSection: DraftPolicySubSection) => {
    if (policySidebarQuery?.data?.state !== 'DISABLED')
      return `${draftManualHelper.getNumberManualNode(policySidebarQuery?.data, reorderedManual as DraftManual)}.${draftManualHelper.getEnabledSectionIndex(section.id, policySidebarQuery?.data as DraftPolicy)}.${draftManualHelper.getEnabledSubSectionIndex(section.id, subSection.id, policySidebarQuery?.data as DraftPolicy)}`;
    return '';
  };

  const isLastSection = (section: DraftPolicySection) => {
    const sections = policySidebarQuery.data?.sections || [];
    return section.id === sections[sections.length - 1]?.id;
  };

  const modalActions = (
    <>
      <Button
        onClick={handleDialogConfirm}
        color='primary'
        autoFocus
      >
        {t('draftManuals.manualStructure.timerModal.ok')}
      </Button>
    </>
  );

  return (
    <>
      <TakeoverLayout
        pageTitle={t('draftManuals.manualStructure.title')}
        backUrl={`/admin/policy/draft-manuals/${reorderedManual?.id}`}
        onBackClick={async () => {
          await unlockManual();
        }}
        contentHeaderLeft={
          <Stack
            direction='row'
            spacing={2}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography>Filter</Typography>
            <Select
              labelId='filter-label'
              id='filter-select'
              value={filter}
              label='Filter'
              size='small'
              onChange={handleFilterChange}
              sx={{
                width: '150px',
              }}
            >
              <MenuItem value={ManualFilterOptions.ALL}>{t('draftManuals.manualStructure.filters.all')}</MenuItem>
              <MenuItem value={ManualFilterOptions.DISABLED}>
                {t('draftManuals.manualStructure.filters.disabled')}
              </MenuItem>
              <MenuItem value={ManualFilterOptions.ENABLED}>
                {t('draftManuals.manualStructure.filters.enabled')}
              </MenuItem>
            </Select>
          </Stack>
        }
        contentHeaderRight={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isSaving && <CircularProgress />}
            <Button
              disabled={isSaving}
              variant='contained'
              color='primary'
              onClick={handleSave}
            >
              {t('common.save')}
            </Button>
          </Box>
        }
        rightDrawerOpen={rightDrawerOpen}
        setRightDrawerOpen={(open) => !open && setSidebarItem(null)}
        rightDrawer={{
          closeable: true,
          isLoading: prefaceSidebarQuery?.isLoading || policySidebarQuery?.isLoading,
          hasError: prefaceSidebarQuery?.isError || policySidebarQuery?.isError,
          error: 'Failed to load content. Please try again.',
          header: (
            <Typography
              sx={{
                width: 'max-content',
                px: 2,
                py: 1,
                fontSize: 12,
                backgroundColor: '#c8e2f7',
                borderRadius: 6,
                color: '#004B87',
                mt: 'auto',
              }}
            >
              {sidebarItemType === 'policy'
                ? 'Policy ' +
                  draftManualHelper.getNumberManualNode(policySidebarQuery?.data, reorderedManual as DraftManual)
                : 'Preface'}
            </Typography>
          ),
          content: (
            <Box sx={{ p: 4, mb: 16 }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{contentData?.title}</Typography>
                {updatedAt && (
                  <Typography sx={{ color: '#697586', fontWeight: 600, fontSize: 14 }}>
                    {updatedAt.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </Typography>
                )}
              </Box>
              {sidebarItem?.type === 'preface' && (
                <Box sx={{ color: '#697586', mt: 3 }}>
                  <Typography sx={{ textWrap: 'auto' }}>
                    {sanitizeContent(prefaceSidebarQuery?.data?.content)}
                  </Typography>
                </Box>
              )}
              {sidebarItem?.type === 'policy' &&
                policySidebarQuery?.data?.sections
                  ?.filter((section) => section.state !== 'DISABLED')
                  .map((section) => (
                    <Box
                      key={section.id}
                      sx={{ mt: 3 }}
                    >
                      <Typography
                        sx={{ fontWeight: 600, fontSize: 16 }}
                      >{`${getSectionIndex(section)} ${section.title}`}</Typography>
                      <Typography sx={{ textWrap: 'auto' }}>{sanitizeContent(section.content)}</Typography>
                      {section.subSections
                        ?.filter((subSection) => subSection.state !== 'DISABLED')
                        .map((subSection, index) => (
                          <Box
                            key={subSection.id}
                            sx={{ ml: 3, mt: 2 }}
                          >
                            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                              {`${getSubSectionIndex(section, subSection)} ${subSection.title}`}
                            </Typography>
                            <Typography sx={{ textWrap: 'auto' }}>{sanitizeContent(subSection.content)}</Typography>
                          </Box>
                        ))}
                    </Box>
                  ))}
            </Box>
          ),
        }}
      >
        <>
          <InactivityDialog
            open={showDialog}
            onCancel={handleDialogConfirm}
            actions={modalActions}
          />
          {reorderedManual ? (
            <>
              <Grid2
                sx={{ px: 4 }}
                container
                spacing={4}
              >
                <Grid2
                  size={{ xs: 12, md: 4 }}
                >
                  <Paper
                    sx={{
                      p: 0.5,
                      borderRadius: '12px',
                      border: '1px solid #e4e8e8',
                      backgroundColor: '#f2f6fa',
                      maxHeight: '64vh',
                    }}
                  >
                    <Paper
                      sx={{
                        p: 4,
                        borderRadius: '12px',
                        border: '1px solid #e4e8e8',
                        maxHeight: '62vh',
                        overflowY: 'auto',
                      }}
                    >
                      <Typography
                        variant='h6'
                        sx={{ mb: 2, display: 'flex', gap: 2 }}
                      >
                        {t('draftManuals.manualStructure.columns.prefaces')}
                        <Typography
                          sx={{
                            my: 'auto',
                            backgroundColor: '#d7e6ea',
                            width: 24,
                            borderRadius: 100,
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          {getFilteredItems('prefaces')?.length}
                        </Typography>
                      </Typography>
                      <Box sx={{}}>
                        <DndContext
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEndPrefaces}
                        >
                          <SortableContext
                            items={getFilteredItems('prefaces').map((p: DraftPrefaceItem) => p.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {(getFilteredItems('prefaces') as DraftPrefaceItem[]).map((preface) => (
                              <SortableItem
                                key={preface.id}
                                id={preface.id}
                              >
                                <DraggableItem
                                  item={preface}
                                  title={preface.title}
                                  itemType='preface'
                                  handleToggleState={handleToggleState}
                                  isSelected={sidebarItem?.item.id === preface.id}
                                  sidebarItem={sidebarItem}
                                  setSidebarItem={setSidebarItem}
                                />
                              </SortableItem>
                            ))}
                          </SortableContext>
                        </DndContext>
                      </Box>
                    </Paper>
                  </Paper>
                </Grid2>
                <Grid2
                  size={{ xs: 12, md: 4 }}
                >
                  <Paper
                    sx={{
                      p: 0.5,
                      borderRadius: '12px',
                      border: '1px solid #e4e8e8',
                      backgroundColor: '#f2f6fa',
                      maxHeight: '64vh',
                    }}
                  >
                    <Paper
                      sx={{
                        p: 4,
                        borderRadius: '12px',
                        border: '1px solid #e4e8e8',
                        maxHeight: '62vh',
                        overflowY: 'auto',
                      }}
                    >
                      <Typography
                        variant='h6'
                        sx={{ mb: 2, display: 'flex', gap: 2 }}
                      >
                        {t('draftManuals.manualStructure.columns.chapters')}
                        <Typography
                          sx={{
                            my: 'auto',
                            backgroundColor: '#d7e6ea',
                            width: 24,
                            borderRadius: 100,
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          {getFilteredItems('chapters')?.length}
                        </Typography>
                      </Typography>
                      <Box>
                        <DndContext
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEndChapters}
                        >
                          <SortableContext
                            items={getFilteredItems('chapters').map((c: DraftChapter) => c.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {(getFilteredItems('chapters') as DraftChapter[]).map((chapter) => (
                              <SortableItem
                                key={chapter.id}
                                id={chapter.id}
                              >
                                <DraggableItem
                                  item={chapter}
                                  title={
                                    draftManualHelper.formatManualNodeTitle(chapter, {
                                      ...reorderedManual,
                                      chapters: reorderedManual.chapters.filter(
                                        (chapter) => chapter.state !== 'DISABLED'
                                      ),
                                    }) as string
                                  }
                                  itemType='chapter'
                                  isSelected={selectedChapter === chapter.id}
                                  handleToggleState={handleToggleState}
                                  handleChapterClick={handleChapterClick}
                                />
                              </SortableItem>
                            ))}
                          </SortableContext>
                        </DndContext>
                      </Box>
                    </Paper>
                  </Paper>
                </Grid2>
                {selectedChapter ? (
                  <Grid2
                    size={{ xs: 12, md: 4 }}
                  >
                    <Paper
                      sx={{
                        p: 0.5,
                        borderRadius: '12px',
                        border: '1px solid #e4e8e8',
                        backgroundColor: '#f2f6fa',
                        maxHeight: '64vh',
                      }}
                    >
                      <Paper
                        sx={{
                          p: 4,
                          borderRadius: '12px',
                          border: '1px solid #e4e8e8',
                          maxHeight: '62vh',
                          overflowY: 'auto',
                        }}
                      >
                        <Typography
                          variant='h6'
                          sx={{ mb: 2, display: 'flex', gap: 2 }}
                        >
                          {t('draftManuals.manualStructure.columns.policies')}
                          <Typography
                            sx={{
                              my: 'auto',
                              backgroundColor: '#d7e6ea',
                              width: 24,
                              borderRadius: 100,
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            {getFilteredItems('policies')?.length}
                          </Typography>
                        </Typography>
                        <Box>
                          <DndContext
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEndPolicies}
                          >
                            <SortableContext
                              items={getFilteredItems('policies').map((p: DraftPolicyItem) => p.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {(getFilteredItems('policies') as DraftPolicyItem[]).map((policy) => (
                                <SortableItem
                                  key={policy.id}
                                  id={policy.id}
                                >
                                  <DraggableItem
                                    item={policy}
                                    title={draftManualHelper.formatManualNodeTitle(policy, reorderedManual) as string}
                                    itemType='policy'
                                    handleToggleState={handleToggleState}
                                    isSelected={sidebarItem?.item.id === policy.id}
                                    sidebarItem={sidebarItem}
                                    setSidebarItem={setSidebarItem}
                                  />
                                </SortableItem>
                              ))}
                            </SortableContext>
                          </DndContext>
                        </Box>
                      </Paper>
                    </Paper>
                  </Grid2>
                ) : (
                  <Grid2
                    size={{ xs: 12, md: 4 }}
                  >
                    <Paper
                      sx={{
                        p: 0.5,
                        borderRadius: '12px',
                        border: '1px solid #e4e8e8',
                        backgroundColor: '#f2f6fa',
                        maxHeight: '64vh',
                      }}
                    >
                      <Paper
                        sx={{
                          p: 4,
                          borderRadius: '12px',
                          border: '1px solid #e4e8e8',
                          backgroundColor: '#f2f6fa',
                          maxHeight: '62vh',
                          overflowY: 'auto',
                        }}
                      >
                        <Typography
                          variant='h6'
                          sx={{ mb: 2, display: 'flex', gap: 2 }}
                        >
                          {t('draftManuals.manualStructure.columns.policies')}
                        </Typography>
                        <Box>
                          <Typography>{t('draftManuals.manualStructure.columns.selectChapter')}</Typography>
                        </Box>
                      </Paper>
                    </Paper>
                  </Grid2>
                )}
              </Grid2>
            </>
          ) : (
            <Loading />
          )}
        </>
      </TakeoverLayout>
    </>
  );
};

export default ManualStructure;
